import {
  useRef, useState, useEffect, useCallback,
  ChangeEvent, KeyboardEvent, CSSProperties,
} from 'react';
import { api } from '../lib/api';

/**
 * AddressAutocomplete — a controlled text input with a US-address suggestions
 * dropdown, backed by the same-origin `/api/address/autocomplete` proxy (Smarty).
 *
 * Design contract:
 *   - Controlled: `value` + `onChange` keep manual entry FULLY working — a user
 *     can ignore suggestions and type freely.
 *   - `onSelect({line1, city, state, zip})` fires when the user picks a
 *     suggestion (or an expanded unit of a multi-unit building).
 *   - Inert/degraded: when the endpoint returns `enabled:false` (unkeyed on the
 *     server) or errors, the component behaves as a plain input — no dropdown
 *     ever. Same-origin only; no external host, so the enforced CSP is untouched.
 *   - Keyboard: ArrowUp/ArrowDown move the highlight, Enter selects, Escape
 *     closes; click (mousedown) selects.
 *
 * Token-styled + dark-first via CSS custom properties, matching the app's inline
 * style system (mirrors the Input atom).
 */

export interface AddressSelection {
  line1: string;
  city: string;
  state: string;
  zip: string;
}

interface Suggestion {
  line1: string;
  city: string;
  state: string;
  zip: string;
  label: string;
  entries: number;
  selectedValue: string;
}

interface AutocompleteResponse {
  enabled: boolean;
  suggestions: Suggestion[];
}

export interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (selection: AddressSelection) => void;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  /** Minimum characters before querying (matches the server's short-input floor). */
  minChars?: number;
  /** Debounce delay for the query, ms. */
  debounceMs?: number;
}

export function AddressAutocomplete({
  value, onChange, onSelect,
  id, placeholder, disabled, invalid,
  minChars = 3, debounceMs = 250,
}: AddressAutocompleteProps) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  // Once the endpoint reports enabled:false we never query again — plain input.
  const [providerDisabled, setProviderDisabled] = useState(false);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // Tidy up timers + in-flight requests on unmount.
  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (abortRef.current) abortRef.current.abort();
  }, []);

  // Close the dropdown on an outside click.
  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  const runQuery = useCallback(async (q: string, selected?: string) => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const params = new URLSearchParams({ q });
      if (selected) params.set('selected', selected);
      const res = await api.get<AutocompleteResponse>(
        `/api/address/autocomplete?${params.toString()}`,
        ctrl.signal,
      );
      if (ctrl.signal.aborted) return;
      if (!res || res.enabled === false) {
        // Server has no Smarty key → degrade to a plain input, permanently.
        setProviderDisabled(true);
        setSuggestions([]);
        setOpen(false);
        return;
      }
      const next = res.suggestions || [];
      setSuggestions(next);
      setHighlight(-1);
      setOpen(next.length > 0);
    } catch {
      // Transient error / abort → behave as a plain input for this keystroke.
      if (!ctrl.signal.aborted) { setSuggestions([]); setOpen(false); }
    }
  }, []);

  const scheduleQuery = useCallback((q: string) => {
    if (providerDisabled) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    if (q.trim().length < minChars) { setSuggestions([]); setOpen(false); return; }
    timerRef.current = setTimeout(() => runQuery(q.trim()), debounceMs);
  }, [providerDisabled, minChars, debounceMs, runQuery]);

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const v = e.target.value;
    onChange(v);          // manual entry always works
    scheduleQuery(v);     // programmatic fills (pick) never call this
  }

  function pick(s: Suggestion) {
    if (s.entries > 1) {
      // Multi-unit building: drill into its individual secondary numbers, then
      // the user picks the exact unit (entries === 1) which fills directly.
      onChange(s.line1);
      runQuery(s.line1, s.selectedValue);
      return;
    }
    onChange(s.line1);
    onSelect({ line1: s.line1, city: s.city, state: s.state, zip: s.zip });
    setSuggestions([]);
    setOpen(false);
    setHighlight(-1);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      if (highlight >= 0 && highlight < suggestions.length) {
        e.preventDefault();
        pick(suggestions[highlight]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      setHighlight(-1);
    }
  }

  const showDropdown = open && !providerDisabled && suggestions.length > 0;
  const listboxId = id ? `${id}-listbox` : undefined;

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <input
        id={id}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        role="combobox"
        aria-expanded={showDropdown}
        aria-autocomplete="list"
        aria-controls={listboxId}
        aria-invalid={invalid || undefined}
        style={inputStyle(invalid)}
      />
      {showDropdown && (
        <ul id={listboxId} role="listbox" style={DROPDOWN_STYLE}>
          {suggestions.map((s, i) => (
            <li
              key={`${s.selectedValue}-${i}`}
              role="option"
              aria-selected={i === highlight}
              // mousedown (not click) so selection beats the input's blur.
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
              onMouseEnter={() => setHighlight(i)}
              style={optionStyle(i === highlight)}
            >
              {s.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function inputStyle(invalid?: boolean): CSSProperties {
  return {
    fontFamily: 'inherit',
    fontSize: 'var(--fs-md)',
    padding: '8px 12px',
    border: `1px solid ${invalid ? 'var(--color-status-red)' : 'var(--color-border)'}`,
    borderRadius: 'var(--r-md)',
    background: 'var(--color-card)',
    color: 'var(--color-ink)',
    outline: 'none',
    transition: 'border-color 120ms ease',
    width: '100%',
  };
}

const DROPDOWN_STYLE: CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  right: 0,
  zIndex: 20,
  margin: 0,
  padding: 'var(--sp-1)',
  listStyle: 'none',
  maxHeight: 240,
  overflowY: 'auto',
  background: 'var(--color-card)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--r-md)',
  boxShadow: '0 6px 20px rgba(0,0,0,0.18)',
};

function optionStyle(active: boolean): CSSProperties {
  return {
    padding: 'var(--sp-2) var(--sp-3)',
    fontSize: 'var(--fs-sm)',
    color: 'var(--color-ink)',
    borderRadius: 'var(--r-sm)',
    cursor: 'pointer',
    background: active ? 'var(--brand-primary-bg)' : 'transparent',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };
}
